import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import { Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { CustomButton, InputTextFieldGrid } from 'components';
import { getSettingsService, updateSettingsService } from 'services/admin/settingsServices';
import { setLoading } from 'redux/slices/uiSlice';
import { errorAlert } from 'helpers/alerts';
import InputChipFieldGrid from 'components/common/InputChipFieldGrid';
import CheckboxFieldGrid from 'components/common/CheckboxFieldGrid';

// Un factor en 0 dejaria a todos los comercios con tope cero y ninguno podria vender. El
// calculo nocturno ya se protege de eso, pero el panel no debe dejar llegar tan lejos.
const factorValido = (value) => {
  const raw = value === undefined || value === null ? '' : String(value).trim();
  if (raw === '') return true;
  if (!/^\d+(\.\d+)?$/.test(raw)) return 'Escribe un número mayor que 0';
  if (Number(raw) <= 0) return 'El factor no puede ser 0: dejaría a todos los comercios sin poder vender';
  return true;
};

// El limite base si puede quedarse vacio: es una decision legitima de operacion.
const limiteBaseValido = (value) => {
  const raw = value === undefined || value === null ? '' : String(value).trim();
  if (raw === '') return true;
  if (!/^\d+(\.\d+)?$/.test(raw)) return 'Escribe un número mayor o igual que 0';
  return true;
};

// Un campo numerico vacio viaja como null, para que quien lo lea distinga «sin valor»
// de un cero escrito a proposito.
const sinValor = (valor) => {
  const raw = valor === undefined || valor === null ? '' : String(valor).trim();
  return raw === '' ? null : Number(raw);
};

const SettingsScreen = () => {
  const dispatch = useDispatch();
  const { control, handleSubmit, reset } = useForm();
  const [id, setId] = useState('');

  useEffect(() => {
    getSettingsService().then((result) => {
      // Ausente se muestra como activo, igual que lo lee el core: apagar el límite es
      // una decisión explícita, nunca lo que pasa por no haber configurado nada.
      reset({ ...result, security_limit_enabled: result.security_limit_enabled !== false });
      setId(result.id);
    }
    ).catch((e) => {
      errorAlert(e);
    }
    )
    // eslint-disable-next-line
  }, []);

  const submit = async (result) => {
    dispatch(setLoading(true));
    updateSettingsService(id, {
      intermittence_close_txns: result.intermittence_close_txns ? parseInt(result.intermittence_close_txns) : 0,
      intermittence_start: result.intermittence_start ? parseInt(result.intermittence_start) : 0,
      intermittence_cooldown: result.intermittence_cooldown ? parseInt(result.intermittence_cooldown) : 0,
      high_txn_time_threshold: result.high_txn_time_threshold ? parseInt(result.high_txn_time_threshold) : 0,
      high_txn_threshold: result.high_txn_threshold ? parseInt(result.high_txn_threshold) : 0,
      primary_provider_test_time: result.primary_provider_test_time ? parseInt(result.primary_provider_test_time) : 0,
      daily_closure_emails: result.daily_closure_emails,
      same_phone_cooldown: result.same_phone_cooldown ? parseInt(result.same_phone_cooldown) : 0,
      // Vacio se guarda como null y no como 0. Un factor en 0 no es «sin valor»: el
      // calculo de madrugada lo respeta —usa `?? 1.5`, que no sustituye el 0— y deja a
      // todos los comercios con limite 0, con lo que el core rechaza cualquier recarga.
      // Es justo lo que impide la validacion cuando el 0 se escribe a mano.
      security_limit_daily_factor: sinValor(result.security_limit_daily_factor),
      security_limit_hourly_factor: sinValor(result.security_limit_hourly_factor),
      security_limit_base: sinValor(result.security_limit_base),
      security_limit_floor: sinValor(result.security_limit_floor),
      security_limit_enabled: Boolean(result.security_limit_enabled),
    }).then((response) => {
    }).catch(error => errorAlert(error))
      .finally(() => dispatch(setLoading(false)));
  }

  return (
    <Grid container direction="column" justifyContent="center" alignItems="center" mt={4}>
      <Grid container direction="row" justifyContent="center" alignItems="center" spacing={4}>
        <Grid item xs={12} md={6}  >
          <Card>
            <CardHeader
              subheader={"Configuración de Intermitencia"}
            />
            <CardContent>
              <Grid container spacing={3}>
                <InputTextFieldGrid
                  xs={12}
                  name={"intermittence_close_txns"}
                  control={control}
                  // required={"Debes agregar el número de transacciones para cerrar intermitencia"}
                  label={"Número de transacciones para cerrar intermitencia"}
                  type="number"
                />
                <InputTextFieldGrid
                  xs={12}
                  name={"intermittence_start"}
                  control={control}
                  // required={"Debes agregar el número de alertas consecutivas para crear intermitencia"}
                  label={"Número de alertas consecutivas para crear intermitencia"}
                  type="number"
                />
                <InputTextFieldGrid
                  xs={12}
                  name={"intermittence_cooldown"}
                  control={control}
                  // required={"Debes agregar el tiempo mínimo entre intermitencias"}
                  label={"Tiempo minimo entre intermitencias (minutos)"}
                  type="number"
                />
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} >
          <Card>
            <CardHeader
              subheader={"Configuración de Tiempos Altos de Transacción"}
            />
            <CardContent>
              <Grid container spacing={3}>
                <InputTextFieldGrid
                  xs={12}
                  label={"Tiempo para considerar una transacción como alta (segundos)"}
                  name={"high_txn_time_threshold"}
                  control={control}
                  // required={"Debes agregar el tiempo para considerar una transacción como alta"}
                  type="number"
                />
                <InputTextFieldGrid
                  xs={12}
                  control={control}
                  label={"Número de transacciones para cambiar a proveedor de respaldo"}
                  name={"high_txn_threshold"}
                  // required={"Debes agregar el número de transacciones para cambiar a proveedor de respaldo"}
                  type="number"
                />
                <InputTextFieldGrid
                  xs={12}
                  label={"Tiempo para probar proveedor principal (minutos)"}
                  name={"primary_provider_test_time"}
                  control={control}
                  // required={"Debes agregar el tiempo para probar el proveedor principal"}
                  type="number"
                />
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} >
          <Card>
            <CardHeader
              subheader={"Configuración de Correo de Cierre Diario"}
            />
            <CardContent>
              <Grid container spacing={3}>
                <InputChipFieldGrid
                  xs={12}
                  name={"daily_closure_emails"}
                  control={control}
                  label={"Correos electrónicos"}
                />
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} >
          <Card>
            <CardHeader
              subheader={"Configuración de Seguridad"}
            />
            <CardContent>
              <Grid container spacing={3}>
                <InputTextFieldGrid
                  xs={12}
                  label={"Tiempo de seguridad entre recargas al mismo celular (minutos)"}
                  name={"same_phone_cooldown"}
                  control={control}
                  rules={{
                    validate: (value) => {
                      const raw = value === undefined || value === null ? '' : String(value).trim();
                      if (raw === '') return true;
                      if (!/^-?\d+$/.test(raw)) return 'Escribe un número entero de minutos';
                      if (parseInt(raw) < 0) return 'El tiempo no puede ser negativo';
                      return true;
                    }
                  }}
                />
                <Grid item xs={12} mt={-2}>
                  <Typography variant="caption" color="text.secondary">
                    Con 0 la regla no se aplica: ninguna recarga se deniega por este motivo.
                  </Typography>
                </Grid>
                <InputTextFieldGrid
                  xs={12}
                  label={"Factor del límite diario por comercio"}
                  name={"security_limit_daily_factor"}
                  control={control}
                  rules={{ validate: factorValido }}
                />
                <InputTextFieldGrid
                  xs={12}
                  label={"Factor del límite por hora por comercio"}
                  name={"security_limit_hourly_factor"}
                  control={control}
                  rules={{ validate: factorValido }}
                />
                <InputTextFieldGrid
                  xs={12}
                  label={"Límite base para comercios sin historial"}
                  name={"security_limit_base"}
                  control={control}
                  rules={{ validate: limiteBaseValido }}
                />
                <InputTextFieldGrid
                  xs={12}
                  label={"Piso del límite por comercio"}
                  name={"security_limit_floor"}
                  control={control}
                  rules={{ validate: limiteBaseValido }}
                />
                <CheckboxFieldGrid
                  xs={12}
                  label={"Aplicar el límite de seguridad en la venta"}
                  name={"security_limit_enabled"}
                  control={control}
                />
                <Grid item xs={12} mt={-2}>
                  <Typography variant="caption" color="text.secondary">
                    Sin esto, ninguna recarga se rechaza por el límite del comercio. Surte
                    efecto en la siguiente venta, sin esperar a la madrugada.
                  </Typography>
                </Grid>
                <Grid item xs={12} mt={-2}>
                  <Typography variant="caption" color="text.secondary">
                    Ningún límite calculado baja de aquí. Sin piso, un comercio que vende poco
                    y en días sueltos queda con un tope de unos pocos pesos.
                  </Typography>
                </Grid>
                <Grid item xs={12} mt={-2}>
                  <Typography variant="caption" color="text.secondary">
                    Los límites de cada comercio se recalculan en la siguiente madrugada: cambiar
                    estos valores no surte efecto al guardar.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid item justifyContent="center" alignItems="center" mt={4}>
        <CustomButton
          name={"Guardar"}
          click={handleSubmit(submit)}
        />
      </Grid>
    </Grid>
  )

}

export default SettingsScreen
